/* eslint-disable max-lines */
import {
  COMPETITOR_CONFIG_OPTIONS,
  INTERVIEW_PRIORITY,
  INTERVIEW_TITLE,
  PORT,
  PROJECT_ROLES,
  QUESTION_ANALYSE_TYPES,
  QUESTION_TYPES,
} from '@constants';

import { Nullable, Option } from './common';
import { AttributeChartPoints, CHART_TYPE, ChartsData, CompareAggregation, ValueMapChartPoints } from './charts';

export interface ApiResponse<T> {
  errorMessage: string;
  errorDescription: string;
  payload: T;
}

export interface Company {
  id: number;
  name: string;
}

export interface Project {
  businessId: number;
  companyId: number;
  description: string;
  role: PROJECT_ROLES;
  companyName: string;
  createdDate: string;
  completedInterviewsCount: number;
  toDoInterviewsCount: number;
  draft: boolean;
}

export interface Projects {
  Request: {
    companyId?: Nullable<number>;
    creationMonth?: string;
  };
  Response: ApiResponse<Project[]>;
}

export interface Interview {
  businessId: number;
  worksheetId: number;
  interviewee: string;
  company: string;
  lang: number;
  completed: boolean;
  requiredFieldsCompleted: boolean;
}

export interface Interviews {
  Request: {
    businessId?: number;
    assignedToMe?: boolean;
    onlyUncompleted?: boolean;
  };
  Response: ApiResponse<Interview[]>;
}

export interface NewComment {
  Request: {
    category: string;
    text: string;
  };
  Response: ApiResponse<CommentType>;
}

export interface EditComment {
  Request: {
    category: string;
    text: string;
    id: number;
  };
  Response: ApiResponse<CommentType>;
}

export interface DeleteComment {
  Request: {
    commentId: number;
  };
  Response: ApiResponse<object>;
}

export interface Segment {
  type: number;
  description: string;
  selectedCode?: Nullable<number>;
  codes: {
    [key: string]: string;
  };
}

export interface CommentType {
  id: number;
  category: string;
  text: string;
}

export interface Comments {
  Response: ApiResponse<CommentType[]>;
}

export interface JointInterview {
  company: string;
  interviewee: string;
}

export enum INTERVIEW_TYPE {
  F2F = 'F2F',
  WEB = 'WEB',
  ZOOM = 'ZOOM',
  TEAMS = 'TEAMS',
  GOOGLE_MEET = 'GOOGLE_MEET',
  PHONE = 'PHONE',
  EXCEL = 'EXCEL',
  OTHER = 'OTHER_VIRTUAL',
}

export interface InterviewData {
  businessId: number;
  worksheetId: number;
  priority: INTERVIEW_PRIORITY;
  interviewType: Nullable<INTERVIEW_TYPE>;
  company: Nullable<string>;
  thisBusinessName: Nullable<string>;
  interviewee: Nullable<string>;
  address: Nullable<string>;
  country: Nullable<string>;
  phone: Nullable<string>;
  url: string;
  isCompleted: boolean;
  lang: string;
  interviewer: Nullable<string>;
  appointmentDate: string;
  email: Nullable<string>;
  gender: Nullable<string>;
  jobTitle: Nullable<string>;
  postcode: Nullable<string>;
  mobileNumber: Nullable<string>;
  salesContact: Nullable<string>;
  segments: Segment[];
  jointInterviews: JointInterview[];
  langCode: number;
  interviewerId: Nullable<number>;
  jointInterviewId: Nullable<number>;
}

export interface InterviewDetails {
  Request: {
    businessId?: number;
    worksheetId?: number;
  };
  Response: ApiResponse<InterviewData>;
}

export interface Segmentation {
  Request: {
    body: {
      type: number;
      selectedCode: Nullable<number>;
    }[];
    businessId?: number;
    worksheetId?: number;
  };
  Response: ApiResponse<object>;
}

export enum SUPPLLIER_TYPE {
  THIS_BUSINESS = 'THIS_BUSINESS',
  SELECTED = 'SELECTED',
  BLANK = 'BLANK',
}

export interface Supplier {
  id: number;
  name?: Nullable<string>;
  relativePrice?: number;
  seq: number;
  supplierType: SUPPLLIER_TYPE;
  shortName?: Nullable<string>;
}

export interface SuppliersList {
  Response: ApiResponse<Supplier[]>;
}

export interface SupplierPayload {
  name: string;
  seq: number;
}

export interface EditSupplier {
  Request: {
    body: SupplierPayload[];
    businessId?: number;
    worksheetId?: number;
  };
  Response: ApiResponse<Supplier[]>;
}

export interface AddSupplier {
  Request: {
    body: { name?: string; seq: number; supplierType: SUPPLLIER_TYPE; relativePrice?: number }[];
    businessId?: number;
    worksheetId?: number;
  };
  Response: ApiResponse<Supplier[]>;
}

export interface DeleteSupplier {
  Request: {
    supplierId: number;
    businessId?: number;
    worksheetId?: number;
  };
  Response: ApiResponse<object>;
}

export interface SaveRelativePrices {
  Request: {
    body: Record<string, number>;
    businessId?: number;
    worksheetId?: number;
  };
  Response: ApiResponse<Nullable<Supplier[]>>;
}

export enum ATTRIBUTE_TYPE {
  PRODUCT = 'PRODUCT',
  SERVICE = 'SERVICE',
  IMAGE = 'IMAGE',
}

export interface AttributeDetails {
  seq: number;
  type: ATTRIBUTE_TYPE;
  name: string;
  importance: number;
  suppliersScores: Record<string, number>;
  description: string;
  note: Nullable<string>;
  included: boolean;
}

export interface AttributeList {
  Response: ApiResponse<AttributeDetails[]>;
  TransformedResponse: AttributeDetails[];
}

export interface SavePriceSensitivity {
  Request: {
    body: number;
    businessId?: number;
    worksheetId?: number;
  };
  Response: ApiResponse<object>;
}

export interface PriceSensitivity {
  Response: ApiResponse<Nullable<number>>;
}

export interface QuestionOption {
  optionId: number;
  optionText: string;
}

export interface AnswerOption {
  id: number;
  text: string;
  selected: boolean;
}

export interface GeneralQuestion {
  seq?: number;
  type?: QUESTION_TYPES;
  question?: string;
  category?: string;
  categoryId?: number;
  options?: QuestionOption[];
}

export interface GeneralQuestions {
  [key: string]: GeneralQuestion[];
}

export interface GeneralQuestionsList {
  Response: ApiResponse<GeneralQuestions>;
}

export interface GeneralAnswer {
  seq?: number;
  type?: QUESTION_TYPES;
  question?: string;
  categoryId?: number;
  value?: number;
  textAnswer?: string;
  options?: AnswerOption[];
}

export interface SaveGeneralAnswers {
  Request: {
    body: GeneralAnswer[];
    businessId?: number;
    worksheetId?: number;
  };
  Response: ApiResponse<object>;
}

export interface GeneralAnswers {
  [key: string]: GeneralAnswer[];
}

export interface GetGeneralAnswers {
  Response: ApiResponse<GeneralAnswers>;
}

export interface SaveAttributeScores {
  Request: {
    body: AttributeDetails;
    businessId?: number;
    worksheetId?: number;
  };
  Response: ApiResponse<object>;
}

export interface CompleteInterview {
  Response: ApiResponse<object>;
}

export interface Localization {
  Request: {
    langCode: number;
    localizationGroup: string;
  };
  Response: ApiResponse<Record<string, string>>;
}

//Project setup

export interface ProjectAttribute {
  attributeSeq?: number;
  attributeName: string;
  attributeDescription?: string;
}

export interface AttributeGroup {
  categoryId: number;
  categoryName: string;
  type: ATTRIBUTE_TYPE;
  attributes: ProjectAttribute[];
}

export interface ProjectAttributes {
  Request: {
    businessId?: number;
  };
  Response: ApiResponse<AttributeGroup[]>;
}

export interface DeleteProjectAttribute {
  Request: {
    businessId: number;
    attributeSeq?: number;
  };
  Response: ApiResponse<object>;
}

interface CreateProjectPayload {
  companyId: number;
  projectName: string;
  businessName: string;
  sourceProjectId?: number;
  copyAttributes?: boolean;
  copyCompetitors?: boolean;
  copySegmentation?: boolean;
  copyQuestions?: boolean;
  copyConfiguration?: boolean;
}

export interface CreateProject {
  Request: {
    body: CreateProjectPayload;
    isDuplicateMode?: boolean;
  };
  Response: ApiResponse<number>;
}

export interface CreateCompany {
  Request: {
    body: string;
  };
  Response: ApiResponse<Company>;
}

export interface SaveProjectAttributes {
  Request: {
    businessId: number;
    body: AttributeGroup[];
  };
  Response: ApiResponse<AttributeGroup[]>;
}

export interface NameUniqueness {
  Request: {
    companyId: number;
    entityType: string;
    name: string;
  };
  Response: ApiResponse<boolean>;
}

export interface SurveyConfiguration {
  titleConfig?: INTERVIEW_TITLE;
  introduction?: string;
  instructions?: string;
  competitorConfig?: COMPETITOR_CONFIG_OPTIONS;
  blankCompetitorCount?: Nullable<number>;
  emailFooter?: string;
  replyEmail?: string;
  friendlyName?: string;
  sendFrom?: string;
  friendlyName2?: string;
  password?: string;
  emailServer?: string;
  emailPort?: PORT;
  selectedCompetitors?: number[];
}

export interface SaveSurveyConfiguration {
  Request: {
    businessId: number;
    body: SurveyConfiguration;
  };
  Response: ApiResponse<SurveyConfiguration>;
}

export interface GetSurveyConfiguration {
  Request: {
    businessId?: number;
  };
  Response: ApiResponse<SurveyConfiguration>;
}

export interface ProjectDetails {
  surveyName: string;
  displayName: Nullable<string>;
  surveyScope: Nullable<string>;
  defaultLangCode: number;
  year: Nullable<number>;
  projectManagerUserId: number;
  businessName: string;
  companyName?: string;
}

export interface GetProjectDetails {
  Request: {
    businessId?: number;
  };
  Response: ApiResponse<ProjectDetails>;
}

export interface SaveProjectDetails {
  Request: {
    businessId?: number;
    body: ProjectDetails;
  };
  Response: ApiResponse<object>;
}

export interface ManagerInfo {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
}

export interface SaveProjectDetails {
  Request: {
    businessId?: number;
    body: ProjectDetails;
  };
  Response: ApiResponse<object>;
}

export interface Staff extends ManagerInfo {
  projectRole: PROJECT_ROLES;
  isActive: boolean;
}

export interface GetStaff {
  Request: {
    businessId?: number;
  };
  Response: ApiResponse<Staff[]>;
}

export interface AddProjectStaff {
  Request: {
    businessId?: number;
    body: {
      userId: number;
      projectRole: PROJECT_ROLES;
    };
  };
  Response: ApiResponse<Staff>;
}

export interface StaffDetails {
  userId?: number;
  firstName: string;
  lastName: string;
  email: string;
  projectRole: Nullable<PROJECT_ROLES>;
  phone: string;
  countryCode: number;
  isActive?: Nullable<boolean>;
  fluentLanguages: number[];
}

export interface UpdateStaff {
  Request: {
    businessId?: number;
    body: StaffDetails;
  };
  Response: ApiResponse<Staff>;
}

export interface GetStaffDetails {
  Request: {
    businessId?: number;
    userId: number;
  };
  Response: ApiResponse<StaffDetails>;
}

export interface EmailUniqueness {
  Request: {
    email: string;
  };
  Response: boolean;
}

export interface ProjectSegments {
  type?: number;
  headerCode?: number;
  description: string;
  codes: Record<string, string>;
}

export interface TransformedProjectSegments {
  type?: number;
  headerCode?: number;
  description: string;
  codes: { key: string; value: string }[];
}

export interface GetProjectSegments {
  Request: {
    businessId?: number;
  };
  Response: ApiResponse<ProjectSegments[]>;
  TransformedResponse: ApiResponse<TransformedProjectSegments[]>;
}

export interface SaveProjectSegments {
  Request: {
    businessId?: number;
    body: ProjectSegments[];
  };
  Response: ApiResponse<object>;
}

export interface DeleteProjectSegment {
  Request: {
    businessId?: number;
    segmentType?: number;
    segmentCode?: string;
  };
  Response: ApiResponse<object>;
}

export interface Competitor {
  competitorSeq: number;
  shortId: string;
  businessName: string;
  competitorId?: number;
  benchmark?: boolean;
  ignored?: boolean;
  assignedCompetitorId?: number;
  marketShare?: number;
}

export interface ProjectCompetitors {
  reorderAble: boolean;
  competitors: Competitor[];
}

export interface GetProjectCompetitors {
  Request: {
    businessId?: number;
  };
  Response: ApiResponse<ProjectCompetitors>;
}

export interface SaveProjectCompetitors {
  Request: {
    businessId?: number;
    body: Competitor[];
  };
  Response: ApiResponse<ProjectCompetitors>;
}

export interface DeleteProjectCompetitor {
  Request: {
    businessId?: number;
    competitorId: number;
  };
}

export interface DeleteQuestion {
  Request: {
    businessId?: number;
    questionId: number;
  };
}

export interface DeleteCategory {
  Request: {
    businessId?: number;
    categoryId: number;
  };
}

interface QuestionOptions {
  optionId: number;
  optionText: string;
}

interface ProjectQuestion {
  seq: number;
  type: QUESTION_TYPES;
  question?: string;
  questionId?: number;
  options: QuestionOptions[];
}

export interface ProjectGeneralQuestion {
  category?: string;
  categoryId?: number;
  categorySeq: number;
  questions: ProjectQuestion[];
}

export interface ProjectQuestions {
  Request: {
    businessId?: number;
  };
  Response: ApiResponse<ProjectGeneralQuestion[]>;
}

export interface SaveGeneralQuestions {
  Request: {
    businessId?: number;
    body: ProjectGeneralQuestion[];
  };
  Response: ApiResponse<object>;
}

export interface FinishProjectSetup {
  Request: {
    businessId?: number;
  };
  Response: ApiResponse<object>;
}

export interface GetCompletedForms {
  Request: {
    businessId?: number;
  };
  Response: ApiResponse<Record<string, boolean>>;
}

export interface FilteredInterviewList {
  businessId?: number;
  onlyUncompleted?: boolean;
  languageCode?: number;
  country?: string;
  interviewerId?: number;
  priority?: INTERVIEW_PRIORITY;
}

export interface DeleteInterview {
  Request: {
    businessId: number;
    worksheetId: number;
  };
}

export interface CreateInterview {
  Request: {
    body: number;
  };
  Response: ApiResponse<number>;
}

export interface EditInterview {
  Request: {
    body: InterviewData;
  };
}

export interface InvitationData {
  fromEmail: string;
  toEmail: string;
  appointmentDate: Date;
  intervieweeName: string;
  companyName: string;
  interviewerName: string;
  optionalReplyEmail: string;
  friendlyEmail: string;
  emailFooter: string;
  token: string;
}

export interface GetInvitationData {
  Request: {
    businessId?: number;
    worksheetId?: number;
  };
  Response: ApiResponse<InvitationData>;
}

export interface GetBenchmarks {
  Request: {
    businessId?: number;
  };
  Response: Competitor[];
}

export interface GetFilterCategories {
  Request: {
    businessId?: number;
  };
  Response: ApiResponse<Record<string, string>>;
  TransformedResponse: Option[];
}

export interface Aggregation {
  businessId: number;
  aggregationId: number;
  name: string;
}

export interface GetAggregations {
  Request: {
    businessId?: number;
    categoryId?: number;
  };
  Response: ApiResponse<Aggregation[]>;
}

export interface GeneralAnalysisAnswer {
  interviewTitle: string;
  value: number;
  answer: string;
  options: Nullable<AnswerOption[]>;
}

export interface GetGeneralAnalysisAnswers {
  Request: {
    businessId?: number;
    aggregationId?: number;
    questionId?: number;
  };
  Response: ApiResponse<GeneralAnalysisAnswer[]>;
}

export interface GetAllAggregations {
  Request: {
    businessId?: number;
  };
  Response: ApiResponse<Aggregation[]>;
}

export interface GetGeneralQuestionAnalysis {
  Request: {
    businessId?: number;
    aggregationId?: number;
    questionId?: number;
    questionAnalysisType?: QUESTION_ANALYSE_TYPES;
  };
  Response: ApiResponse<string>;
}

export interface GetChartsData {
  Request: {
    businessId?: number;
    aggregationId?: number;
    benchmarkId?: number;
  };
  Response: ApiResponse<ChartsData>;
}

export interface GetWordCloudAnalysis {
  Request: {
    businessId?: number;
    aggregationId?: number;
    questionId?: number;
  };
  Response: ApiResponse<Record<string, number>>;
}

export interface GetAnalysisScenario {
  Request: {
    businessId?: number;
    aggregationId?: number;
    benchmarkId?: number;
  };
  Response: ApiResponse<ScenarioDto>;
}

export interface PriceAdjustmentDto {
  competitorId?: number;
  priceAdjust?: number;
}

export interface GetChangedScenarioAnalisysData {
  Request: {
    businessId?: number;
    aggregationId?: number;
    body: ScenarioAdjustmentDto;
    benchmarkId?: number;
  };
  Response: ApiResponse<ScenarioDto>;
}

export interface ScenarioAdjustmentDto {
  priceImportance?: number;
  priceAdjustments?: PriceAdjustmentDto[];
  scoresAdjustments?: ScoreAdjustmentDto[];
}

export interface ScoreAdjustmentDto {
  competitorId: number;
  attributeId?: number;
  scoreAdjust?: number;
}

export interface ScenarioDto {
  priceImportance: Nullable<number>;
  competitors: Nullable<Record<string, string>>;
  valueMap: Nullable<ScenarioValueMap>;
  priceChanges: Nullable<ScenarioTableRecord[]>;
  attributeScores: Nullable<Record<string, Record<string, ScenarioTableRecord[]>>>;
}

export interface ScenarioTableRecord {
  id: number;
  name: string;
  originalValue: number;
  currentValue: number;
  changedValue: number;
}

export interface ScenarioValueMap {
  priceImportance: number;
  qualityImportance: number;
  marketDifference: number;
  fairValueLine: Record<string, number>;
  chartPoints: ValueMapChartPointDto[];
  fitLine: Record<string, number>;
}

export interface ValueMapChartPointDto {
  id: number;
  name: string;
  price: Nullable<number>;
  quality: Nullable<number>;
  shortName: string;
  marketShare: Nullable<number>;
  mentionsCount: number;
  similarPoints?: ValueMapChartPoints[];
}

export interface AddCategory {
  Request: {
    body: {
      businessId?: number;
      name: string;
      assignAggregations: number[];
    };
  };
  Response: ApiResponse<number>;
}

export interface EditCategory {
  Request: {
    businessId?: number;
    body: Record<string, string>;
  };
  Response: ApiResponse<Record<string, string>>;
}

export interface DeleteCategories {
  Request: {
    businessId?: number;
    categoryIds: number[];
  };
  Response: ApiResponse<object>;
}

export interface CategoryUniqueness {
  Request: {
    businessId?: number;
    name: string;
  };
  Response: ApiResponse<boolean>;
}

export interface GetIndividualResults {
  Request: {
    businessId?: number;
    worksheetId?: number;
    benchmarkId?: number;
  };
  Response: ApiResponse<ChartsData>;
}

export interface AttributeNote {
  worksheetId: number;
  interviewTitle: string;
  attributeNote: string | null;
}

export interface Attribute {
  attributeSeq: number;
  attributeName: string;
  attributeNotes: AttributeNote[];
}

export interface AttributesByCategory {
  [key: string]: Attribute[];
  PRODUCT: Attribute[];
  SERVICE: Attribute[];
  IMAGE: Attribute[];
}

export interface GetAggregatedAttributeNotesData {
  Request: {
    businessId?: number;
    aggregationId?: number;
  };
  Response: ApiResponse<AttributesByCategory>;
}

export interface GetAggregatedAttributeNotesBySeqData {
  Request: {
    businessId?: number;
    aggregationId?: number;
    attributeSeq?: number;
  };
  Response: ApiResponse<Attribute>;
}

export interface GetAttributeAnalysisData {
  Request: {
    businessId?: number;
    aggregationId?: number;
    attributeSeq?: number;
    benchmarkId?: number;
  };
  Response: ApiResponse<AttributeChartPoints[]>;
}

export interface SegmentCode {
  key: string;
  value: string;
}

export interface CompletenessData {
  supplierId: number;
  supplierName: string;
  worksheetsInclusionMap: Record<string, boolean>;
}

export interface GetCompletenessData {
  Request: {
    businessId?: number;
    aggregationId?: number;
  };
  Response: ApiResponse<CompletenessData[]>;
}

export interface AggregatedSegments {
  type: number;
  segmentCodes: number[];
}

export interface NewAggregation {
  businessId?: number;
  name: string;
  year: number;
  categoryId: string;
  worksheetIds: number[];
  aggregatedSegments: AggregatedSegments[];
}

export interface SaveAggregation {
  Request: {
    body: NewAggregation;
  };
  Response: ApiResponse<object>;
}

export interface CheckAggregationName {
  Request: {
    businessId?: number;
    name: string;
  };
  Response: ApiResponse<boolean>;
}

interface SeriesOption {
  id: number;
  value: string;
}

interface AxisData {
  title: Nullable<string>;
  categories: Nullable<string[]>;
}

export interface TimeSeriesChartData {
  type: CHART_TYPE;
  title: string;
  xaxis: AxisData;
  yaxis: AxisData;
  series: {
    name: string;
    data: number[];
  }[];
}

interface InitailTimeSeriesData {
  qualityTypes: SeriesOption[];
  competitors: SeriesOption[];
  charts: TimeSeriesChartData[];
}

interface TimeSeriesData {
  qualityTypes: Option[];
  competitors: Option[];
  charts: TimeSeriesChartData[];
}

export interface GetTimeSeriesCharts {
  Request: {
    businessId?: number;
    aggregationId: Nullable<number>;
    benchmarkId?: number;
  };
  Response: ApiResponse<InitailTimeSeriesData>;
  TransformedResponse: TimeSeriesData;
}

export interface GetTimeSeriesQuality {
  Request: {
    businessId?: number;
    aggregationId: Nullable<number>;
    benchmarkId?: number;
    qualityType: number;
  };
  Response: ApiResponse<TimeSeriesChartData>;
}

export interface GetTimeSeriesDifferences {
  Request: {
    businessId?: number;
    aggregationId: Nullable<number>;
    benchmarkId?: number;
    competitorId: number;
  };
  Response: ApiResponse<TimeSeriesChartData>;
}

export interface DeleteAggregations {
  Request: {
    businessId: number;
    aggregationIds: number[];
  };
  Response: ApiResponse<object>;
}

export interface AggregatedItemDetails {
  uniqueKey: string;
  key: number;
  type: number;
  segment: string;
  value: string;
  selected: boolean;
}

export interface AggregationDetails {
  name: string;
  year: number;
  categoryId: number;
  segments: AggregatedItemDetails[];
  worksheets: AggregatedItemDetails[];
}

export interface GetAggregationDetails {
  Request: {
    businessId?: number;
    aggregationId: Nullable<number>;
  };
  Response: ApiResponse<AggregationDetails>;
}

export interface EditAggregation {
  Request: {
    businessId?: number;
    aggregationId: number;
    body: NewAggregation;
  };
  Response: ApiResponse<object>;
}

export interface GetValueComparison {
  Request: {
    businessId?: number;
    benchmarkId?: number;
    aggregationId1?: number;
    aggregationId2?: number;
  };
  Response: ApiResponse<Record<string, CompareAggregation>>;
}

export interface GetChartsExport {
  Request: {
    body: {
      fileName: string;
      chartsDataMatrix: string[][];
    };
  };
  Response: Blob;
}

export interface GetIntervieweeTemplate {
  Request: {
    businessId: number;
  };
  Response: ApiResponse<string>;
}

export interface GetInterviewTemplate {
  Request: {
    businessId: number;
    worksheetId?: number;
  };
  Response: ApiResponse<string>;
}

export interface ImportIntervieweeData {
  Request: {
    businessId: number;
    body: FormData;
  };
  Response: ApiResponse<number[]>;
}

export interface UpdateIntervieweeData {
  Request: {
    businessId: number;
    worksheetId: number;
    body: FormData;
  };
  Response: ApiResponse<number>;
}

export interface UpdateInterviewData {
  Request: {
    businessId: number;
    worksheetId: number;
    body: FormData;
  };
  Response: ApiResponse<object>;
}

export interface TranslationLanguage {
  id: number;
  name: string;
}

export interface GeneralTranslationLanguages {
  id: number;
  name: string;
  existingLanguageList: TranslationLanguage[];
  newLanguageList: TranslationLanguage[];
}

export interface ProjectTranslationLanguages {
  projectName: string;
  baseLanguage: TranslationLanguage;
  existingLanguageList: TranslationLanguage[];
  newLanguageList: TranslationLanguage[];
}

export interface TranslationLanguagesOverview {
  generalTranslations: GeneralTranslationLanguages[];
  projectTranslation: ProjectTranslationLanguages;
}

export interface GetGeneralOverview {
  Response: ApiResponse<TranslationLanguagesOverview>;
}

export interface GetProjectOverview {
  Request: {
    businessId?: number;
  };
  Response: ApiResponse<TranslationLanguagesOverview>;
}

export interface TranslationContent {
  textKey: string;
  sourceText: string;
  translatedText: string;
}

export interface TranslationContentList {
  fromLanguage: TranslationLanguage;
  toLanguage: TranslationLanguage;
  content: TranslationContent[];
}

export interface GetGeneralTranslations {
  Request: {
    languageId: string;
    languagePool: string;
  };
  Response: ApiResponse<TranslationContentList>;
}

export interface GetProjectTranslationData {
  Request: {
    languageId: string;
    businessId?: number;
    languagePool: string;
  };
  Response: ApiResponse<TranslationContentList>;
}

export interface TranslationContentMutation {
  textKey?: string;
  sourceText?: string;
  translatedText?: string;
}

export interface TranslateGeneralContentTranslation {
  Request: {
    languageId: string;
    languagePool?: string;
    body: TranslationContentMutation;
  };
  Response: ApiResponse<TranslationContent>;
}

export interface TranslateProjectContentData {
  Request: {
    languageId: string;
    languagePool?: string;
    businessId?: number;
    body: TranslationContentMutation;
  };
  Response: ApiResponse<TranslationContent>;
}

export interface AddGeneralPoolLanguage {
  Request: {
    languageId: string;
    languagePool: string;
  };
}

export interface AddProjectPoolLanguage {
  Request: {
    languageId: string;
    businessId?: number;
  };
}

export interface SaveGeneralTranslations {
  Request: {
    languageId?: string;
    languagePool?: string;
    body: TranslationContentMutation;
  };
}

export interface SaveProjectTranslations {
  Request: {
    languagePool: string;
    languageId?: string;
    businessId?: number;
    body: TranslationContentMutation;
  };
}
export interface UserPermissions {
  isPimsAdmin: boolean;
  adminCompanyId: Nullable<number>;
}

export interface GetUserPermissions {
  Response: ApiResponse<UserPermissions>;
}

export interface MeetingRequestPayload {
  copyEmail: string;
  mailSubject: string;
  mailContent: string;
  userTimeZone: string;
}

export interface SendMeetingRequest {
  Request: {
    body: MeetingRequestPayload;
    businessId?: number;
    worksheetId?: number;
  };
}

export interface ExportAnalysisData {
  Request: {
    businessId: number;
    aggregationId: number;
    benchmarkId: number;
    languageCode: number;
  };
  Response: ApiResponse<string>;
}
